import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Fade,
  LinearProgress,
  Chip,
  Collapse,
  TextField,
  Tooltip,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowBack,
  HelpOutline,
  Info,
  ExpandMore,
  ExpandLess,
  Description,
  TrendingUp,
  RestartAlt,
} from '@mui/icons-material';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import SchoolIcon from '@mui/icons-material/School';
import BuildIcon from '@mui/icons-material/Build';
import AssessmentIcon from '@mui/icons-material/Assessment';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk';
import BugReportIcon from '@mui/icons-material/BugReport';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { QuestionData, AssessmentData, Rating } from '../services/questionService';
import { loadQuestions } from '../services/questionService';
import ReactMarkdown from 'react-markdown';
import './ContentBoxes.css';
import MDEditor, { commands } from '@uiw/react-md-editor';

const ratingIcons: Record<string, React.ElementType> = {
  knowledge: SchoolIcon,
  process: BuildIcon,
  metrics: AssessmentIcon,
  adoption: TrendingUpIcon,
  automation: AutoGraphIcon,
};

const ratingColors: Record<string, string> = {
  knowledge: '#2196f3', // Blue
  process: '#4caf50',   // Green
  metrics: '#ff9800',   // Orange
  adoption: '#9c27b0',  // Purple
  automation: '#f44336', // Red
};

// Remove or comment out the helpSectionIcons if not used
// const helpSectionIcons: Record<string, React.ElementType> = {
//   overview: Info,
//   details: Description,
//   considerations: Lightbulb,
//   challenges: Warning,
//   maturity: TrendingUp,
// };

const helpSectionColors: Record<string, string> = {
  overview: '#2196f3', // Blue
  details: '#4caf50',  // Green
  considerations: '#9c27b0', // Purple
  challenges: '#f44336', // Red
  maturity: '#ff9800', // Orange
};

const getMaturityLevel = (score: number, maxScore: number) => {
  if (maxScore === 0) return { level: 'Crawl', color: '#ff9800' }; // Orange
  const percentage = (score / maxScore) * 100;
  if (percentage < 30) return { level: 'Crawl', color: '#ff9800' }; // Orange
  if (percentage < 60) return { level: 'Walk', color: '#2196f3' };  // Blue
  return { level: 'Run', color: '#4caf50' };                        // Green
};

const VIEW_CONFIG = {
  // the default view is to not show scores
  showScores: process.env.REACT_APP_SHOW_SCORES === 'true' ? true : false,
};


const Questionnaire: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [questions, setQuestions] = useState<QuestionData[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [assessmentData, setAssessmentData] = useState<AssessmentData | null>(null);
  const [answers, setAnswers] = useState<Record<string, Record<string, number>>>({});
  const [loading, setLoading] = useState(true);
  // Remove or comment out the slideDirection if not used
  // const [slideDirection, setSlideDirection] = useState<'left' | 'right'>('right');
  const [error, setError] = useState<string | null>(null);
  const [helpOpen, setHelpOpen] = useState(true);
  const [expandedSection, setExpandedSection] = useState<string | null>('details');
  const [showComments, setShowComments] = useState<Record<string, boolean>>({});
  const [comments, setComments] = useState<Record<string, string>>({});
  const [showRatingsAndComments, setShowRatingsAndComments] = useState(true);

  useEffect(() => {
    // Load questions from location state
    if (location.state?.questions) {
      setQuestions(location.state.questions);
      console.log('Questions loaded:', location.state.questions);
    } else {
      setError('No questions provided. Please go back and select domains and audiences.');
    }

    // Load assessment data
    const loadAssessment = async () => {
      try {
        const data = await loadQuestions();
        setAssessmentData(data);
      } catch (error) {
        console.error('Failed to load assessment data:', error);
        setError('Failed to load assessment data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadAssessment();

    // set default view
    setShowRatingsAndComments(VIEW_CONFIG.showScores);
  }, [location.state]);

  // Load answers from localStorage if available
  useEffect(() => {
    try {
      const savedAnswers = localStorage.getItem('answers');
      const savedComments = localStorage.getItem('comments');

      if (savedAnswers) {
        setAnswers(JSON.parse(savedAnswers));
      }
      if (savedComments) {
        setComments(JSON.parse(savedComments));
      }
    } catch (error) {
      console.error('Failed to load saved data:', error);
    }
  }, []);

  // Update save to localStorage to include comments
  useEffect(() => {
    if (Object.keys(answers).length > 0) {
      localStorage.setItem('answers', JSON.stringify(answers));
    }
    if (Object.keys(comments).length > 0) {
      localStorage.setItem('comments', JSON.stringify(comments));
    }
  }, [answers, comments]);

  // Initialize the answer all questions with all categories set to 0 if answer not already exist
  useEffect(() => {
    if (questions.length > 0) {
      const categories = ['knowledge', 'process', 'metrics', 'adoption', 'automation'];
      const targetAnswers: Record<string, Record<string, number>> = {};

      questions.forEach(question => {
        const questionId = question.id;
        // Initialize with all categories set to 0
        const currentAnswer = answers[questionId];

        if (!currentAnswer) {
          targetAnswers[questionId] = {};
          categories.forEach(category => {
            targetAnswers[questionId][category] = 0;
          });
        } else {
          // shallow copy
          targetAnswers[questionId] = {
            ...currentAnswer,
          };
        }
      });

      setAnswers(targetAnswers);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questions]);

  const handleBack = () => {
    navigate('/domains');
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      // Keep the slideDirection state but comment out its usage
      // setSlideDirection('left');
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      // Keep the slideDirection state but comment out its usage
      // setSlideDirection('right');
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const handleRatingChange = (ratingType: string, value: number) => {
    if (!currentQuestion) return;
    const questionId = currentQuestion.id;
    setAnswers(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        [ratingType]: value
      }
    }));
  };

  const getTotalScore = () => {
    return Object.values(answers).reduce((sum, questionAnswers) => {
      const categories = ['knowledge', 'process', 'metrics', 'adoption', 'automation'];
      const questionScore = categories.reduce((qSum, category) => {
        return qSum + questionAnswers[category];
      }, 0);
      return sum + questionScore;
    }, 0);
  };

  const getMaxScore = () => {
    if (!assessmentData) return 0;
    const maxRating = 5; // Maximum score per rating type
    const categories = ['knowledge', 'process', 'metrics', 'adoption', 'automation'];

    // Calculate max score for all questions (each question has 5 categories, max 5 points each)
    return questions.length * categories.length * maxRating;
  };

  const getProgressPercentage = () => {
    if (!questions || questions.length === 0) return 0;
    return ((currentQuestionIndex + 1) / questions.length) * 100;
  };


  const handleViewResults = () => {
    // Ensure all questions have answers
    const completeAnswers = { ...answers };
    questions.forEach(question => {
      if (!completeAnswers[question.id]) {
        completeAnswers[question.id] = {};
        Object.entries(assessmentData?.ratings || {}).forEach(([ratingType, ratings]) => {
          if (ratings.length > 0) {
            completeAnswers[question.id][ratingType] = ratings[0].value;
          }
        });
      }
    });

    const metadata = {
      date: new Date().toISOString(),
      description: '',
      cloudAccounts: [],
      contactName: '',
      contactEmail: ''
    };

    navigate('/results', {
      state: {
        answers: completeAnswers,
        questions,
        metadata,
        comments,
        showScores: showRatingsAndComments,
        assessmentData,
      }
    });
  };

  const areAllQuestionsAnswered = () => {
    if (!questions || questions.length === 0) return false;

    return Object.keys(answers).length > 0;
  };

  const handleReset = () => {
    // Clear local storage
    localStorage.removeItem('answers');
    localStorage.removeItem('comments');
    // Reset state
    setAnswers({});
    setComments({});
    setCurrentQuestionIndex(0);
  };

  const handleCommentChange = (questionId: string, comment: string) => {
    setComments(prev => ({
      ...prev,
      [questionId]: comment
    }));
  };

  const handleCommentToggle = (questionId: string) => {
    setShowComments(prev => ({
      ...prev,
      [questionId]: !prev[questionId]
    }));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!assessmentData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography color="error">No assessment data available</Typography>
      </Box>
    );
  }

  if (!questions || questions.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography color="error">No questions available</Typography>
      </Box>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  if (!currentQuestion) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography color="error">Invalid question index</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{
      p: { xs: 2, sm: 3, md: 4 },
      maxWidth: 1600,
      margin: '0 auto',
      width: '100%',
      minHeight: '100vh',
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '300px',
        zIndex: 0,
        borderRadius: '0 0 50% 50% / 0 0 20px 20px'
      }
    }}>
      <Box sx={{
        display: 'flex',
        gap: 3,
        position: 'relative',
        zIndex: 1
      }}>
        {/* Main Content Area */}
        <Box sx={{
          flexGrow: 1,
          width: helpOpen ? { xs: '100%', md: '67%' } : '100%',
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
          {/* Header */}
          <Box className="header-box" sx={{
            display: 'flex',
            alignItems: 'center',
            mb: 4,
            p: 3,
            borderRadius: 3,
            backdropFilter: 'blur(10px)',
            transition: 'all 0.3s ease-in-out'
          }}>
            <IconButton
              onClick={handleBack}
              sx={{
                mr: 2,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                  transform: 'scale(1.05) rotate(-5deg)',
                  boxShadow: '0 4px 20px rgba(102, 126, 234, 0.4)'
                }
              }}
              title="Back to Domains"
            >
              <ArrowBack />
            </IconButton>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' },
                letterSpacing: '-0.5px',
                lineHeight: 1.2,
                mb: 0,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                animation: 'gradient 3s ease infinite',
                backgroundSize: '200% 200%',
                '@keyframes gradient': {
                  '0%': { backgroundPosition: '0% 50%' },
                  '50%': { backgroundPosition: '100% 50%' },
                  '100%': { backgroundPosition: '0% 50%' }
                }
              }}
            >
              {assessmentData?.name || "Assessment"}
            </Typography>
            <Box sx={{
              ml: 'auto',
              display: 'flex',
              alignItems: 'center',
              gap: 2
            }}>
              {/* Toggle button for showing ratings and comments */}
              <Tooltip
                title={showRatingsAndComments ? "Hide ratings and comments" : "Show ratings and comments"}
                arrow
                placement="bottom"
              >
                <IconButton
                  onClick={() => setShowRatingsAndComments(!showRatingsAndComments)}
                  sx={{
                    border: `2px solid ${showRatingsAndComments ? theme.palette.secondary.main : theme.palette.text.secondary}`,
                    color: showRatingsAndComments ? 'secondary.main' : 'text.secondary',
                    backgroundColor: showRatingsAndComments ? 'secondary.50' : 'transparent',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      borderColor: 'secondary.main',
                      backgroundColor: 'secondary.50',
                      transform: 'scale(1.05)',
                      boxShadow: '0 4px 12px rgba(156, 39, 176, 0.3)'
                    }
                  }}
                >
                  <AssessmentIcon />
                </IconButton>
              </Tooltip>

              {/* Current maturity display - only show when toggle is on */}
              {showRatingsAndComments && (() => {
                const maturity = getMaturityLevel(getTotalScore(), getMaxScore());
                const getMaturityIcon = () => {
                  switch (maturity.level) {
                    case 'Crawl':
                      return <BugReportIcon sx={{ fontSize: 32 }} />;
                    case 'Walk':
                      return <DirectionsWalkIcon sx={{ fontSize: 32 }} />;
                    case 'Run':
                      return <DirectionsRunIcon sx={{ fontSize: 32 }} />;
                    case 'Fly':
                      return <RocketLaunchIcon sx={{ fontSize: 32 }} />;
                    default:
                      return <EmojiEventsIcon sx={{ fontSize: 32 }} />;
                  }
                };

                return (
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 2,
                    borderRadius: 3,
                    background: `linear-gradient(135deg, ${maturity.color}10 0%, ${maturity.color}20 100%)`,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 24px ${maturity.color}30`
                    }
                  }}>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      background: `linear-gradient(135deg, ${maturity.color} 0%, ${maturity.color}dd 100%)`,
                      color: 'white',
                      boxShadow: `0 4px 12px ${maturity.color}40`
                    }}>
                      {getMaturityIcon()}
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          color: 'text.secondary',
                          mb: 0.5
                        }}
                      >
                        Current Level
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 800,
                          lineHeight: 1,
                          color: maturity.color,
                          fontSize: '1.25rem'
                        }}
                      >
                        {maturity.level}
                      </Typography>
                    </Box>
                  </Box>
                );
              })()}
            </Box>
          </Box>

          {/* Progress Bar */}
          <Paper className="progress-box" sx={{
            p: 3,
            mb: 4,
            borderRadius: 3,
            backdropFilter: 'blur(10px)',
            transition: 'all 0.3s ease-in-out'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                Question {currentQuestionIndex + 1} of {questions.length}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {Math.round(getProgressPercentage())}%
                </Typography>
                <IconButton
                  onClick={() => setHelpOpen(!helpOpen)}
                  sx={{
                    background: helpOpen
                      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                      : 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                    color: helpOpen ? 'white' : '#667eea',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      background: helpOpen
                        ? 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)'
                        : 'linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.2) 100%)',
                      transform: 'scale(1.1) rotate(10deg)',
                      boxShadow: '0 4px 20px rgba(102, 126, 234, 0.4)'
                    }
                  }}
                  title={helpOpen ? "Hide Help" : "Show Help"}
                >
                  <HelpOutline />
                </IconButton>
              </Box>
            </Box>
            <LinearProgress
              variant="determinate"
              value={getProgressPercentage()}
              sx={{
                height: 10,
                borderRadius: 5,
                backgroundColor: 'rgba(102, 126, 234, 0.1)',
                overflow: 'hidden',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 5,
                  background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                  boxShadow: '0 0 10px rgba(102, 126, 234, 0.5)'
                }
              }}
            />

            {/* Rating Chips - only show when showRatingsAndComments is true */}
            {showRatingsAndComments && (
              <Box className="rating-chips-container" sx={{
                mt: 3,
                p: 3,
                borderRadius: 3
              }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    mb: 2,
                    fontWeight: 700,
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontSize: '0.75rem'
                  }}
                >
                  Current Ratings
                </Typography>
                <Box sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 2,
                  alignItems: 'center'
                }}>
                  {Object.entries(assessmentData.ratings).map(([ratingType, ratings]) => {
                    const RatingIcon = ratingIcons[ratingType];
                    const ratingColor = ratingColors[ratingType];
                    const currentValue = answers[currentQuestion.id]?.[ratingType];

                    return (
                      <Chip
                        key={ratingType}
                        icon={<RatingIcon sx={{
                          color: currentValue ? 'white' : ratingColor,
                          fontSize: 20,
                          filter: currentValue ? 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' : 'none'
                        }} />}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                color: currentValue ? 'white' : ratingColor,
                                fontSize: '0.875rem'
                              }}
                            >
                              {ratingType.charAt(0).toUpperCase() + ratingType.slice(1)}:
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{
                                color: currentValue ? 'white' : 'text.primary',
                                fontWeight: 700,
                                fontSize: '1rem'
                              }}
                            >
                              {currentValue || '0'}
                            </Typography>
                          </Box>
                        }
                        sx={{
                          backgroundColor: currentValue ? ratingColor : `${ratingColor}10`,
                          border: `2px solid ${ratingColor}`,
                          borderRadius: 2,
                          py: 2.5,
                          px: 1.5,
                          boxShadow: currentValue
                            ? `0 6px 20px ${ratingColor}40, inset 0 1px 0 rgba(255,255,255,0.3)`
                            : `0 2px 8px ${ratingColor}20`,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            backgroundColor: currentValue ? `${ratingColor}ee` : `${ratingColor}20`,
                            transform: 'translateY(-4px) scale(1.05)',
                            boxShadow: currentValue
                              ? `0 12px 32px ${ratingColor}50, inset 0 1px 0 rgba(255,255,255,0.4)`
                              : `0 8px 24px ${ratingColor}30`,
                          },
                          '& .MuiChip-icon': {
                            color: currentValue ? 'white' : ratingColor,
                            marginLeft: 0.5,
                            marginRight: 0
                          },
                          '& .MuiChip-label': {
                            color: currentValue ? 'white' : 'text.primary',
                            px: 1.5,
                            py: 0.5
                          }
                        }}
                        variant="filled"
                      />
                    );
                  })}
                </Box>
              </Box>
            )}
          </Paper>


          {/* Question Content */}
          <Fade in={true} timeout={600}>
            <Paper className="question-box" sx={{
              p: 4,
              borderRadius: 3,
              backdropFilter: 'blur(10px)',
              transition: 'all 0.3s ease-in-out'
            }}>

              <Typography
                variant="h5"
                gutterBottom
                sx={{
                  fontWeight: 900,
                  fontSize: { xs: '1.55rem', sm: '1.75rem' },
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  mb: 3,
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: '-8px',
                    left: 0,
                    width: '60px',
                    height: '4px',
                    // background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: '2px'
                  }
                }}
              >
                {currentQuestion?.domain.name} - {currentQuestion?.capability.name}
              </Typography>
              <Typography
                variant="h5"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '1.25rem', sm: '1.5rem' },
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  mb: 3,
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: '-8px',
                    left: 0,
                    width: '60px',
                    height: '4px',
                    background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: '2px'
                  }
                }}
              >
                {currentQuestion?.question}
              </Typography>

              {/* Comments Button and Text Area - only show when showRatingsAndComments is true */}
              {showRatingsAndComments && (
                <Box sx={{ mt: 2, mb: 4 }} data-color-mode="light">
                  <Typography variant="subtitle2" gutterBottom sx={{ color: 'text.secondary', ml: 1 }}>
                    Comments
                  </Typography>
                  <MDEditor
                    value={comments[currentQuestion.id] || ''}
                    onChange={(value) => handleCommentChange(currentQuestion.id, value || '')}
                    height={150}        
                    minHeight={100}   
                    preview="live"
                    tabSize={2}

                    // customise toolbar
                    commands={[
                      commands.bold,
                      commands.italic,
                      commands.divider,
                      commands.unorderedListCommand,
                      commands.orderedListCommand,
                    ]}
                    style={{ 
                        boxShadow: 'none', 
                        border: '1px solid #c4c4c4',
                        borderRadius: '4px' 
                    }} 
                  />
                </Box>
              )}

              {/* Ratings - only show when showRatingsAndComments is true */}
              {showRatingsAndComments && (
                <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {Object.entries(assessmentData.ratings).map(([ratingType, ratings]) => {
                    const RatingIcon = ratingIcons[ratingType];
                    const ratingColor = ratingColors[ratingType];

                    return (
                      <FormControl key={ratingType} fullWidth>
                        <InputLabel color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <RatingIcon sx={{ color: ratingColor }} />
                          {ratingType.charAt(0).toUpperCase() + ratingType.slice(1)}
                        </InputLabel>
                        <Select
                          value={answers[currentQuestion.id]?.[ratingType] || ''}
                          label={ratingType.charAt(0).toUpperCase() + ratingType.slice(1)}
                          onChange={(e) => handleRatingChange(ratingType, Number(e.target.value))}
                          sx={{
                            '& .MuiSelect-select': {
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 1,
                              backgroundColor: 'white'
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: ratingColor,
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: ratingColor,
                            },
                            backgroundColor: 'white'
                          }}
                        >
                          {ratings.map((rating: Rating) => (
                            <MenuItem
                              key={rating.value}
                              value={rating.value}
                              sx={{
                                '&:hover': {
                                  backgroundColor: `${ratingColor}08`,
                                },
                                '&.Mui-selected': {
                                  backgroundColor: `${ratingColor}15`,
                                  '&:hover': {
                                    backgroundColor: `${ratingColor}22`,
                                  }
                                },
                                backgroundColor: 'white'
                              }}
                            >
                              <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                py: 1.5,
                                borderLeft: `3px solid ${ratingColor}`,
                                pl: 2,
                                width: '100%',
                                backgroundColor: 'white',
                                transition: 'background-color 0.2s'
                              }}>
                                <Typography
                                  variant="body1"
                                  color="text.primary"
                                  sx={{
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word',
                                    fontWeight: 500
                                  }}
                                >
                                  {rating.label}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word',
                                    mt: 1,
                                    lineHeight: 1.4
                                  }}
                                >
                                  {rating.description}
                                </Typography>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    );
                  })}
                </Box>
              )}

              {/* Help Section - show in main area when ratings/comments are hidden */}
              {!showRatingsAndComments && (
                <Box sx={{ mt: 4, mb: 4 }}>
                  <Paper className="help-section-main" sx={{
                    p: 3,
                    borderRadius: 3,
                    backdropFilter: 'blur(10px)',
                    transition: 'all 0.3s ease-in-out'
                  }}>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 3,
                      pb: 2,
                      borderBottom: '2px solid',
                      borderImage: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%) 1'
                    }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text'
                        }}
                      >
                        Help & Guidance
                      </Typography>
                    </Box>
                    {currentQuestion && (
                      <Box>
                        <Box sx={{ mb: 2 }}>
                          <Box
                            className="help-section-item"
                            onClick={() => setExpandedSection(expandedSection === 'overview' ? null : 'overview')}
                            sx={{
                              p: 1.5,
                              backgroundColor: `${helpSectionColors.overview}15`,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              cursor: 'pointer',
                              borderRadius: 1,
                              border: `1px solid ${helpSectionColors.overview}`,
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                              '&:hover': {
                                backgroundColor: `${helpSectionColors.overview}25`,
                                boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                              }
                            }}
                          >
                            <Info sx={{ color: helpSectionColors.overview, fontSize: '1.2rem' }} />
                            <Typography variant="body2" sx={{ color: helpSectionColors.overview, fontWeight: 600, flexGrow: 1 }}>
                              Overview
                            </Typography>
                            {expandedSection === 'overview' ? <ExpandLess /> : <ExpandMore />}
                          </Box>
                          <Collapse in={expandedSection === 'overview'}>
                            <Box sx={{ p: 2, backgroundColor: `${helpSectionColors.overview}08`, borderRadius: 1, border: `1px solid ${helpSectionColors.overview}`, boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                              <ReactMarkdown components={{
                                p: ({ node, ...props }) => <Typography variant="body2" sx={{ mb: 1, fontSize: '0.875rem' }} {...props} />,
                                li: ({ node, ...props }) => <Typography component="li" variant="body2" sx={{ fontSize: '0.875rem', mb: 0.5 }} {...props} />,
                                strong: ({ node, ...props }) => <Typography component="span" variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }} {...props} />,
                              }}>{currentQuestion.help.overview}</ReactMarkdown>
                            </Box>
                          </Collapse>
                        </Box>

                        {/* Details Section */}
                        <Box sx={{ mb: 2 }}>
                          <Box
                            className="help-section-item"
                            onClick={() => setExpandedSection(expandedSection === 'details' ? null : 'details')}
                            sx={{
                              p: 1.5,
                              backgroundColor: `${helpSectionColors.details}15`,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              cursor: 'pointer',
                              borderRadius: 1,
                              border: `1px solid ${helpSectionColors.details}`,
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                              '&:hover': {
                                backgroundColor: `${helpSectionColors.details}25`,
                                boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                              }
                            }}
                          >
                            <Description sx={{ color: helpSectionColors.details, fontSize: '1.2rem' }} />
                            <Typography variant="body2" sx={{ color: helpSectionColors.details, fontWeight: 600, flexGrow: 1 }}>
                              Details
                            </Typography>
                            {expandedSection === 'details' ? <ExpandLess /> : <ExpandMore />}
                          </Box>
                          <Collapse in={expandedSection === 'details'}>
                            <Box sx={{ p: 2, backgroundColor: `${helpSectionColors.details}08`, borderRadius: 1, border: `1px solid ${helpSectionColors.details}`, boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                              <ReactMarkdown components={{
                                p: ({ node, ...props }) => <Typography variant="body2" sx={{ mb: 1, fontSize: '0.875rem' }} {...props} />,
                                li: ({ node, ...props }) => <Typography component="li" variant="body2" sx={{ fontSize: '0.875rem', mb: 0.5 }} {...props} />,
                                strong: ({ node, ...props }) => <Typography component="span" variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }} {...props} />,
                              }}>{currentQuestion.help.details}</ReactMarkdown>
                            </Box>
                          </Collapse>
                        </Box>

                        {/* Maturity Section */}
                        {currentQuestion.help.maturity && (
                          <Box sx={{ mb: 2 }}>
                            <Box
                              className="help-section-item"
                              onClick={() => setExpandedSection(expandedSection === 'maturity' ? null : 'maturity')}
                              sx={{
                                p: 1.5,
                                backgroundColor: `${helpSectionColors.maturity}15`,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                cursor: 'pointer',
                                borderRadius: 1,
                                border: `1px solid ${helpSectionColors.maturity}`,
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                '&:hover': {
                                  backgroundColor: `${helpSectionColors.maturity}25`,
                                  boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                                }
                              }}
                            >
                              <TrendingUp sx={{ color: helpSectionColors.maturity, fontSize: '1.2rem' }} />
                              <Typography variant="body2" sx={{ color: helpSectionColors.maturity, fontWeight: 600, flexGrow: 1 }}>
                                Maturity Levels
                              </Typography>
                              {expandedSection === 'maturity' ? <ExpandLess /> : <ExpandMore />}
                            </Box>
                            <Collapse in={expandedSection === 'maturity'}>
                              <Box sx={{ p: 2, backgroundColor: `${helpSectionColors.maturity}08`, borderRadius: 1, border: `1px solid ${helpSectionColors.maturity}`, boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                                {Object.entries(currentQuestion.help.maturity).map(([level, items]) => (
                                  <Box key={level} sx={{ mb: 2 }}>
                                    <Typography variant="body2" sx={{ color: helpSectionColors.maturity, fontWeight: 600, mb: 1, fontSize: '0.875rem' }}>
                                      {level.charAt(0).toUpperCase() + level.slice(1)}
                                    </Typography>
                                    <Box component="ul" sx={{
                                      pl: 2,
                                      m: 0,
                                      '& li': {
                                        position: 'relative',
                                        pl: '1.5rem',
                                        mb: 1,
                                        '&::before': {
                                          content: '"•"',
                                          position: 'absolute',
                                          left: 0,
                                          color: helpSectionColors.maturity,
                                          fontWeight: 'bold',
                                          fontSize: '1.2rem',
                                          lineHeight: 1.5
                                        }
                                      }
                                    }}>
                                      {items.map((item, index) => (
                                        <Typography
                                          component="li"
                                          key={index}
                                          variant="body2"
                                          sx={{
                                            color: 'text.secondary',
                                            lineHeight: 1.5,
                                            fontSize: '0.875rem',
                                            display: 'list-item',
                                            listStyleType: 'none'
                                          }}
                                        >
                                          {item}
                                        </Typography>
                                      ))}
                                    </Box>
                                  </Box>
                                ))}
                              </Box>
                            </Collapse>
                          </Box>
                        )}
                      </Box>
                    )}
                  </Paper>
                </Box>
              )}

              {/* Navigation Controls */}
              <Box className="navigation-controls" sx={{
                display: 'flex',
                justifyContent: 'space-between',
                mt: 4,
                gap: 2,
                p: 2,
                borderRadius: 2,
                border: `1px solid ${theme.palette.primary.light}`,
                boxShadow: theme.shadows[1],
              }}>
                <Button
                  variant="outlined"
                  onClick={handleBack}
                  startIcon={<ArrowBack />}
                  sx={{
                    borderColor: 'primary.main',
                    color: 'primary.main',
                    '&:hover': {
                      borderColor: 'primary.dark',
                      backgroundColor: 'primary.50'
                    }
                  }}
                >
                  Back to Domains
                </Button>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={handleReset}
                    startIcon={<RestartAlt />}
                    sx={{
                      borderColor: 'error.main',
                      color: 'error.main',
                      '&:hover': {
                        borderColor: 'error.dark',
                        backgroundColor: 'error.50'
                      }
                    }}
                  >
                    Reset Assessment
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handlePrevious}
                    disabled={currentQuestionIndex === 0}
                    sx={{
                      borderColor: 'primary.main',
                      color: 'primary.main',
                      '&:hover': {
                        borderColor: 'primary.dark',
                        backgroundColor: 'primary.50'
                      }
                    }}
                  >
                    Previous
                  </Button>
                  {currentQuestionIndex < questions.length - 1 && (
                    <Button
                      variant="contained"
                      onClick={handleNext}
                      endIcon={<SkipNextIcon />}
                      sx={{
                        backgroundColor: 'primary.main',
                        '&:hover': {
                          backgroundColor: 'primary.dark'
                        }
                      }}
                    >
                      Next
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    onClick={handleViewResults}
                    disabled={!areAllQuestionsAnswered()}
                    endIcon={<AssessmentIcon />}
                    sx={{
                      backgroundColor: 'secondary.main',
                      '&:hover': {
                        backgroundColor: 'secondary.dark'
                      }
                    }}
                  >
                    View Results
                  </Button>
                </Box>
              </Box>
            </Paper>
          </Fade>
        </Box>

        {/* Help Sidebar - only show when ratings/comments are visible */}
        {helpOpen && showRatingsAndComments && (
          <Box sx={{
            width: { xs: '100%', md: '38%' },
            display: { xs: 'none', md: 'block' },
            position: 'sticky',
            top: 0,
            alignSelf: 'flex-start',
            maxHeight: '100vh',
            overflowY: 'auto',
            animation: 'slideInRight 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            '@keyframes slideInRight': {
              '0%': {
                opacity: 0,
                transform: 'translateX(30px)'
              },
              '100%': {
                opacity: 1,
                transform: 'translateX(0)'
              }
            }
          }}>
            <Paper className="help-sidebar" sx={{
              p: 3,
              borderRadius: 3,
              backdropFilter: 'blur(10px)',
              transition: 'all 0.3s ease-in-out'
            }}>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 3,
                pb: 2,
                borderBottom: '2px solid',
                borderImage: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%) 1'
              }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                  }}
                >
                  Help & Guidance
                </Typography>
              </Box>
              {currentQuestion && (
                <Box>
                  <Box sx={{ mb: 2 }}>
                    <Box
                      className="help-section-item"
                      onClick={() => setExpandedSection(expandedSection === 'overview' ? null : 'overview')}
                      sx={{
                        p: 1.5,
                        backgroundColor: `${helpSectionColors.overview}15`,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        cursor: 'pointer',
                        borderRadius: 1,
                        border: `1px solid ${helpSectionColors.overview}`,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        '&:hover': {
                          backgroundColor: `${helpSectionColors.overview}25`,
                          boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                        }
                      }}
                    >
                      <Info sx={{ color: helpSectionColors.overview, fontSize: '1.2rem' }} />
                      <Typography variant="body2" sx={{ color: helpSectionColors.overview, fontWeight: 600, flexGrow: 1 }}>
                        Overview
                      </Typography>
                      {expandedSection === 'overview' ? <ExpandLess /> : <ExpandMore />}
                    </Box>
                    <Collapse in={expandedSection === 'overview'}>
                      <Box sx={{ p: 2, backgroundColor: `${helpSectionColors.overview}08`, borderRadius: 1, border: `1px solid ${helpSectionColors.overview}`, boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                        <ReactMarkdown components={{
                          p: ({ node, ...props }) => <Typography variant="body2" sx={{ mb: 1, fontSize: '0.875rem' }} {...props} />,
                          li: ({ node, ...props }) => <Typography component="li" variant="body2" sx={{ fontSize: '0.875rem', mb: 0.5 }} {...props} />,
                          strong: ({ node, ...props }) => <Typography component="span" variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }} {...props} />,
                        }}>{currentQuestion.help.overview}</ReactMarkdown>
                      </Box>
                    </Collapse>
                  </Box>

                  {/* Details Section */}
                  <Box sx={{ mb: 2 }}>
                    <Box
                      className="help-section-item"
                      onClick={() => setExpandedSection(expandedSection === 'details' ? null : 'details')}
                      sx={{
                        p: 1.5,
                        backgroundColor: `${helpSectionColors.details}15`,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        cursor: 'pointer',
                        borderRadius: 1,
                        border: `1px solid ${helpSectionColors.details}`,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        '&:hover': {
                          backgroundColor: `${helpSectionColors.details}25`,
                          boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                        }
                      }}
                    >
                      <Description sx={{ color: helpSectionColors.details, fontSize: '1.2rem' }} />
                      <Typography variant="body2" sx={{ color: helpSectionColors.details, fontWeight: 600, flexGrow: 1 }}>
                        Details
                      </Typography>
                      {expandedSection === 'details' ? <ExpandLess /> : <ExpandMore />}
                    </Box>
                    <Collapse in={expandedSection === 'details'}>
                      <Box sx={{ p: 2, backgroundColor: `${helpSectionColors.details}08`, borderRadius: 1, border: `1px solid ${helpSectionColors.details}`, boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                        <ReactMarkdown components={{
                          p: ({ node, ...props }) => <Typography variant="body2" sx={{ mb: 1, fontSize: '0.875rem' }} {...props} />,
                          li: ({ node, ...props }) => <Typography component="li" variant="body2" sx={{ fontSize: '0.875rem', mb: 0.5 }} {...props} />,
                          strong: ({ node, ...props }) => <Typography component="span" variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }} {...props} />,
                        }}>{currentQuestion.help.details}</ReactMarkdown>
                      </Box>
                    </Collapse>
                  </Box>

                  {/* Maturity Section */}
                  {currentQuestion.help.maturity && (
                    <Box sx={{ mb: 2 }}>
                      <Box
                        className="help-section-item"
                        onClick={() => setExpandedSection(expandedSection === 'maturity' ? null : 'maturity')}
                        sx={{
                          p: 1.5,
                          backgroundColor: `${helpSectionColors.maturity}15`,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          cursor: 'pointer',
                          borderRadius: 1,
                          border: `1px solid ${helpSectionColors.maturity}`,
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          '&:hover': {
                            backgroundColor: `${helpSectionColors.maturity}25`,
                            boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                          }
                        }}
                      >
                        <TrendingUp sx={{ color: helpSectionColors.maturity, fontSize: '1.2rem' }} />
                        <Typography variant="body2" sx={{ color: helpSectionColors.maturity, fontWeight: 600, flexGrow: 1 }}>
                          Maturity Levels
                        </Typography>
                        {expandedSection === 'maturity' ? <ExpandLess /> : <ExpandMore />}
                      </Box>
                      <Collapse in={expandedSection === 'maturity'}>
                        <Box sx={{ p: 2, backgroundColor: `${helpSectionColors.maturity}08`, borderRadius: 1, border: `1px solid ${helpSectionColors.maturity}`, boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                          {Object.entries(currentQuestion.help.maturity).map(([level, items]) => (
                            <Box key={level} sx={{ mb: 2 }}>
                              <Typography variant="body2" sx={{ color: helpSectionColors.maturity, fontWeight: 600, mb: 1, fontSize: '0.875rem' }}>
                                {level.charAt(0).toUpperCase() + level.slice(1)}
                              </Typography>
                              <Box component="ul" sx={{
                                pl: 2,
                                m: 0,
                                '& li': {
                                  position: 'relative',
                                  pl: '1.5rem',
                                  mb: 1,
                                  '&::before': {
                                    content: '"•"',
                                    position: 'absolute',
                                    left: 0,
                                    color: helpSectionColors.maturity,
                                    fontWeight: 'bold',
                                    fontSize: '1.2rem',
                                    lineHeight: 1.5
                                  }
                                }
                              }}>
                                {items.map((item, index) => (
                                  <Typography
                                    component="li"
                                    key={index}
                                    variant="body2"
                                    sx={{
                                      color: 'text.secondary',
                                      lineHeight: 1.5,
                                      fontSize: '0.875rem',
                                      display: 'list-item',
                                      listStyleType: 'none'
                                    }}
                                  >
                                    {item}
                                  </Typography>
                                ))}
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </Collapse>
                    </Box>
                  )}
                </Box>
              )}
            </Paper>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Questionnaire;
